import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

const menuItems = [
  { icon: "ri-dashboard-3-line", label: "대시보드", path: "/dashboard" },
  { icon: "ri-send-plane-line", label: "문자 발송", path: "/dashboard/send" },
  { icon: "ri-history-line", label: "발송 내역", path: "/dashboard/history" },
  { icon: "ri-contacts-book-2-line", label: "주소록", path: "/dashboard/contacts" },
  { icon: "ri-calendar-schedule-line", label: "예약 발송", path: "/dashboard/schedule" },
  { icon: "ri-smartphone-line", label: "3사 테스트", path: "/dashboard/carrier-test" },
  { icon: "ri-bar-chart-2-line", label: "통계", path: "/dashboard/stats" },
];

const bottomItems = [
  { icon: "ri-settings-3-line", label: "설정", path: "/dashboard/settings" },
  { icon: "ri-customer-service-2-line", label: "고객 지원", path: "/dashboard/support" },
  { icon: "ri-shield-user-line", label: "관리자 콘솔", path: "/admin" },
];

const agencyItem = { icon: "ri-building-4-line", label: "에이전시", path: "/agency" };

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileSlideMenu({ isOpen, onClose }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin, isAgency, session } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [credit, setCredit] = useState<number | null>(null);

  useEffect(() => {
    if (!user || !session) return;
    supabase.functions.invoke("get-user-profile", { body: { user_id: user.id } })
      .then(({ data }) => {
        if (data?.credit !== undefined) setCredit(data.credit);
      })
      .catch(() => {});
  }, [user, session]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/");
    } catch (e) {
      console.error("[MobileSlideMenu] 로그아웃 실패:", e);
    } finally {
      setSigningOut(false);
    }
  };

  const getUserInitial = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) return name.charAt(0).toUpperCase();
    return (user?.email ?? "U").charAt(0).toUpperCase();
  };

  const getUserName = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) return name;
    return user?.email?.split("@")[0] ?? "사용자";
  };

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Slide menu */}
      <div className="md:hidden fixed left-0 top-0 bottom-0 z-50 w-72 bg-[#0d0d1a] border-r border-white/[0.08] flex flex-col animate-in slide-in-from-left duration-300">
        {/* Logo + Close */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/[0.07] shrink-0">
          <Link to="/" onClick={onClose} className="flex items-center gap-2.5 cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 text-white font-bold text-sm shrink-0">
              S
            </div>
            <span className="text-white font-semibold text-sm whitespace-nowrap">Signal SMS</span>
          </Link>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* User info */}
        <div className="px-5 py-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white text-sm font-semibold shrink-0">
              {getUserInitial()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-medium truncate">{getUserName()}</p>
              <p className="text-gray-500 text-xs truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 cursor-pointer transition-all shrink-0"
            >
              <i className={`text-base ${signingOut ? "ri-loader-4-line animate-spin" : "ri-logout-box-r-line"}`}></i>
            </button>
          </div>
          {credit !== null && (
            <div className="mt-3 flex items-center gap-2 bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20 rounded-xl px-3 py-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-wallet-3-line text-violet-400 text-sm"></i>
              </div>
              <span className="text-white text-sm font-semibold">₩{credit.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-3 space-y-0.5">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left cursor-pointer whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-violet-600/30 to-pink-600/20 text-white border border-violet-500/20"
                      : "text-gray-400 hover:text-white hover:bg-white/[0.05]"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-base ${isActive ? "text-violet-400" : "text-current"}`}></i>
                  </div>
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}

            {isAgency && (
              <button
                onClick={() => handleNav(agencyItem.path)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left cursor-pointer whitespace-nowrap transition-all ${
                  location.pathname === agencyItem.path
                    ? "bg-gradient-to-r from-amber-600/30 to-orange-600/20 text-white border border-amber-500/20"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${agencyItem.icon} text-base ${location.pathname === agencyItem.path ? "text-amber-400" : "text-current"}`}></i>
                </div>
                <span className="text-sm">{agencyItem.label}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 whitespace-nowrap">AGENCY</span>
              </button>
            )}
          </div>

          <div className="mx-4 my-4 h-px bg-white/[0.06]"></div>

          <div className="px-3 space-y-0.5">
            {bottomItems.map((item) => {
              // 모바일에서는 관리자 콘솔 메뉴 완전히 숨김 (PC 전용)
              if (item.path === "/admin") return null;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left cursor-pointer whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-white/[0.08] text-white"
                      : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="border-t border-white/[0.07] p-3 shrink-0">
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 cursor-pointer transition-all"
          >
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              {signingOut ? (
                <i className="ri-loader-4-line animate-spin text-sm"></i>
              ) : (
                <i className="ri-logout-box-r-line text-sm"></i>
              )}
            </div>
            <span className="text-sm">{signingOut ? "로그아웃 중..." : "로그아웃"}</span>
          </button>
        </div>
      </div>
    </>
  );
}